import { ChangeDetectionStrategy, Component, computed, effect, inject, input, untracked, type OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import type { IProductDataV3 } from '@df/catalog/product/product';
import { Status } from '@df/core/status';
import { AttributeLabelPipeService } from '@df/ui/common/attribute-label.pipe';
import { optionLabelPipe } from '@df/ui/common/option-label.pipe';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ActionComponent } from '../../../../../ui/api/action.component';
import type { ProductDecorator } from '../../../../model/product/product.decorator';
import { ProductLayoutWishlistService } from './product-layout-wishlist.service';

@Component({
    selector: 'product-layout-wishlist',
    templateUrl: './product-layout-wishlist.component.html',
    styleUrl: './product-layout-wishlist.component.scss',
    host: {
        '[class.hidden]': 'hidden()'
    },
    imports: [ActionComponent, ICmsClientModule, RouterLink, SizeClassDirective, TranslatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductLayoutWishlistComponent extends UiComponent implements OnInit {
    readonly product = input.required<ProductDecorator<IProductDataV3>>();
    /**
     * Component is hidden if there is no message
     */
    readonly hidden = computed(() => !this.productLayoutWishlistService.message());

    /**
     * Message types
     */
    readonly login = computed(() => this.productLayoutWishlistService.message() === 'login');
    readonly beforeAdd = computed(() => this.productLayoutWishlistService.message() === 'beforeAdd');
    readonly addSuccess = computed(() => this.productLayoutWishlistService.message() === 'addSuccess');
    readonly removeSuccess = computed(() => this.productLayoutWishlistService.message() === 'removeSuccess');
    readonly already = computed(() => this.productLayoutWishlistService.message() === 'already');

    /**
     * Option value and label
     */
    readonly optionValue = computed(() => this.productLayoutWishlistService.optionValue());
    readonly optionLabel = computed(() => optionLabelPipe(this.optionValue(), this.missingAttrCode()) ?? '');

    /**
     * Missing attribute code and label
     */
    readonly missingAttrCode = computed(() => this.productLayoutWishlistService.missingAttrCode() ?? '');
    readonly missingAttrLabel = computed(() => {
        const missingAttrCode = this.missingAttrCode();

        if (missingAttrCode) {
            return this.attributeLabelPipeService.execute(missingAttrCode);
        }

        return '';
    });

    /**
     * Status
     */
    readonly status = new Status();

    /**
     * Inject services
     */
    private readonly productLayoutWishlistService = inject(ProductLayoutWishlistService);
    private readonly attributeLabelPipeService = inject(AttributeLabelPipeService);

    constructor() {
        super();

        effect(() => {
            const _state = this.productLayoutWishlistService.state();

            untracked(() => {
                this.detectChanges();
            });
        });
    }

    /**
     * Remove from wishlist
     */
    remove(): void {
        this.productLayoutWishlistService.remove(this.product());
    }
}
